import React, { useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ToastContainer } from '../ui/toast';
import Sidebar from './Sidebar';
import ThemeToggle from '../ThemeToggle';

const Layout: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { toasts, hideToast } = useToast();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = () => {
    logout();
    // Show logout message
    // Note: This will be handled by the auth context redirect
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  // Get user role display
  const getUserRoleDisplay = () => {
    const userRole = user?.roleName || user?.role?.name || null;
    
    switch (userRole) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'partner': return 'Partner';
      case 'user': return 'User';
      default: return 'User';
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onClose={hideToast} />
      <div className="flex bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100 min-h-screen">
        <div className="md:hidden fixed top-3 left-3 z-50">
          <button
            onClick={() => setMobileOpen(true)}
            className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow"
            aria-label="Open sidebar"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700 dark:text-gray-200">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>

        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />

        <div className="flex-1 min-w-0 flex flex-col h-screen">
          <header className="flex-shrink-0 sticky top-0 z-30 h-14 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 supports-[backdrop-filter]:dark:bg-gray-900/60">
            <div className="h-full px-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setCollapsed((v) => !v)} 
                  className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" 
                  aria-label="Toggle sidebar"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700 dark:text-gray-200">
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                </button>
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="currentColor"/>
                    </svg>
                  </div>
                  <span className="font-semibold text-lg bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    TruckTrack Admin
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <ThemeToggle />
                
                {/* User Info */}
                <div className="hidden sm:flex items-center gap-3 text-sm">
                  <div className="text-right">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {getUserDisplayName()}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {getUserRoleDisplay()}
                    </div>
                  </div>
                  
                  {/* User Avatar */}
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium shadow-md">
                    {getUserDisplayName().charAt(0).toUpperCase()}
                  </div>
                </div>
                
                <button 
                  onClick={handleLogout} 
                  className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Logout
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto px-4 py-6">
            <div className="max-w-6xl mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default Layout;
